import { createFileRoute, Link } from "@tanstack/react-router";
import { BUSINESS } from "@/lib/business";
import { PhoneButton } from "@/components/site/PhoneButton";
import { QuoteForm } from "@/components/site/QuoteForm";
import { TrustBar } from "@/components/site/TrustBar";
import { CTABand } from "@/components/site/CTABand";
import { RelatedReading } from "@/components/site/RelatedReading";
import { FAQ } from "@/components/site/FAQ";
import { CITIES } from "@/lib/cities";
import { serviceSchema, breadcrumbSchema, faqSchema } from "@/components/site/SchemaJsonLd";
import techWorking from "@/assets/tech-working.webp";
import { Check } from "lucide-react";

const DESC =
  "Licensed Phoenix plumbing contractors for new construction, remodels, and repairs. Free on-site quotes, fast scheduling. Call 602-834-1208.";
const TITLE = "New Construction Plumbing Phoenix AZ | Phoenix Plumbing Co.";

const FAQS = [
  { q: "What types of projects do you handle?", a: "New homes, additions, remodels, casitas, tenant improvements, guest houses, and small commercial spaces in Phoenix and nearby Valley cities." },
  { q: "Do you work directly with homeowners and builders?", a: "Yes. We work with homeowners, general contractors, remodelers, and property owners. We review plans, walk the site, and coordinate scheduling with your other trades." },
  { q: "Do you pull permits?", a: "Yes — when code requires a permit, we pull it so the work is on record." },
  { q: "Can you quote a project from plans?", a: "Yes. Send us the plans and a scope and we can provide a written plumbing estimate." },
];

export const Route = createFileRoute("/new-construction-plumbing-phoenix-az")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://www.phoenixplumbing.co/new-construction-plumbing-phoenix-az" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: "https://www.phoenixplumbing.co/new-construction-plumbing-phoenix-az" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(serviceSchema("New Construction Plumbing", DESC)) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "New Construction Plumbing", url: "/new-construction-plumbing-phoenix-az" },
      ])) },
    ],
  }),
  component: NewConstructionPage,
});

function NewConstructionPage() {
  return (
    <>
      <section className="relative bg-gradient-to-b from-secondary to-secondary/90 text-secondary-foreground overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-40 bg-cover bg-center" style={{ backgroundImage: `url(${techWorking})` }} />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/85 to-secondary/45" />
        <div className="relative mx-auto max-w-7xl px-4 py-12 md:py-16 grid gap-10 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <p className="text-accent font-bold text-sm uppercase tracking-widest">Phoenix, AZ</p>
            <h1 className="mt-2 text-3xl md:text-5xl font-extrabold tracking-tight">
              New Construction Plumbing in Phoenix, AZ
            </h1>
            <p className="mt-5 text-lg text-secondary-foreground/90 leading-relaxed">
              Phoenix Plumbing Co. provides new construction plumbing for homes, additions,
              remodels, casitas, tenant improvements, and small commercial projects in Phoenix, AZ.
              We help with rough-in plumbing, drain lines, water lines, gas lines, fixtures, water
              heaters, and final plumbing connections.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <PhoneButton size="lg" />
              <a href="#quote" className="inline-flex items-center justify-center rounded-md border-2 border-accent px-6 py-4 text-base font-extrabold text-accent hover:bg-accent hover:text-accent-foreground transition-colors">
                Request a Plans Review
              </a>
            </div>
            <div className="mt-6"><TrustBar dark /></div>
          </div>
          <div id="quote"><QuoteForm defaultService="New Construction Plumbing" /></div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-12 prose prose-lg">
        <p>
          New construction plumbing needs planning, clean layout work, and code-compliant
          installation. Our team works with homeowners, builders, remodelers, and property owners
          to keep plumbing projects moving without confusion.
        </p>

        <h2>Plumbing for New Builds, Additions & Remodels</h2>
        <p>
          We can help with plumbing installation for new homes, bathroom additions, kitchen
          remodels, laundry rooms, garage conversions, guest houses, casitas, and small commercial
          spaces. Every project is different, so we review the layout, fixture locations, water
          supply needs, drain requirements, venting, and access points before work begins.
        </p>
        <p>Our work may include:</p>
        <ul>
          <li>Underground plumbing</li>
          <li>Rough-in plumbing</li>
          <li>Water supply lines</li>
          <li>Drain and waste lines</li>
          <li>Vent lines</li>
          <li>Gas line installation</li>
          <li>Water heater connections</li>
          <li>Shower, tub, toilet, and sink plumbing</li>
          <li>Kitchen and laundry plumbing</li>
          <li>Fixture installation</li>
          <li>Final trim-out plumbing</li>
        </ul>

        <h2>Why Proper Planning Matters</h2>
        <p>
          Bad plumbing layout can cause problems after walls, cabinets, tile, and flooring are
          already installed. That can lead to delays, extra costs, and repairs that could have
          been avoided. Phoenix Plumbing Co. focuses on proper planning before the work starts.
        </p>
        <p>
          We check fixture locations, drainage slope, shutoff access, water heater placement,
          pipe routing, and future service access. This helps create a cleaner project and a
          better finished result.
        </p>

        <h2>Our New Construction Plumbing Process</h2>
        <ol>
          <li>Review the project plans, layout, and plumbing needs.</li>
          <li>Walk the property or job site when needed.</li>
          <li>Confirm fixture locations, water lines, drain lines, and gas line needs.</li>
          <li>Provide a clear plumbing estimate.</li>
          <li>Complete rough-in plumbing and required connections.</li>
          <li>Return for final fixture installation and trim-out when the project is ready.</li>
        </ol>

        <h2>Related Services</h2>
        <p>
          Pair new construction plumbing with{" "}
          <Link to="/gas-line-installation-and-repair-phoenix-az" className="text-primary font-semibold">gas line installation</Link>,{" "}
          <Link to="/faucet-and-fixture-installation-phoenix-az" className="text-primary font-semibold">faucet and fixture installation</Link>,{" "}
          <Link to="/kitchen-and-bathroom-plumbing" className="text-primary font-semibold">kitchen and bathroom plumbing</Link>, and{" "}
          <Link to="/water-heater-installation-and-repair" className="text-primary font-semibold">water heater installation</Link>. Or{" "}
          <Link to="/contact" className="text-primary font-semibold">contact Phoenix Plumbing Co.</Link> to discuss your project.
        </p>

        <h2>Call Phoenix Plumbing Co. for New Construction Plumbing</h2>
        <p>
          Planning a new build, addition, remodel, or casita in Phoenix? Call Phoenix Plumbing Co.
          at <a href={BUSINESS.phoneHref} className="text-primary font-semibold">{BUSINESS.phone}</a> to
          discuss your plumbing project.
        </p>
      </section>

      <section className="mx-auto max-w-4xl px-4 pb-12">
        <h2 className="text-2xl md:text-3xl font-extrabold text-secondary mb-6">Frequently Asked Questions</h2>
        <FAQ items={FAQS} />
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-12">
        <h2 className="text-xl font-extrabold text-secondary mb-3">Service Areas</h2>
        <p className="text-muted-foreground mb-4">
          New construction plumbing across Phoenix and the Valley.
        </p>
        <div className="grid gap-2 sm:grid-cols-3 md:grid-cols-4">
          {CITIES.map((c) => (
            <Link
              key={c.slug}
              to="/areas-served/$city"
              params={{ city: c.slug }}
              className="rounded-md border border-border bg-card px-3 py-2 text-sm font-semibold text-secondary hover:border-primary hover:text-primary transition-colors flex items-center gap-2"
            >
              <Check className="h-4 w-4 text-primary" /> {c.name}
            </Link>
          ))}
        </div>
      </section>

      <RelatedReading slug="new-construction-plumbing-phoenix-az" />
      <CTABand title="Planning a project in Phoenix?" />
    </>
  );
}